if (!customElements.get('color-picker-property')) {
  /* ---------------------------------------------------------------------------
     Farb-Hilfsfunktionen (HSV <-> RGB <-> HEX)
     --------------------------------------------------------------------------- */
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function hsvToRgb(h, s, v) {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return [
      Math.round((r + m) * 255),
      Math.round((g + m) * 255),
      Math.round((b + m) * 255),
    ];
  }

  function rgbToHsv(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : d / max;
    return [h, s, max];
  }

  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map((x) => clamp(x, 0, 255).toString(16).padStart(2, '0')).join('').toUpperCase();
  }

  function hexToRgb(hex) {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return null;
    return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
  }

  /* ---------------------------------------------------------------------------
     Custom Element
     --------------------------------------------------------------------------- */
  class ColorPickerProperty extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-trigger]');
      this.triggerText = this.querySelector('[data-trigger-text]');
      this.swatch = this.querySelector('[data-swatch]');
      this.resetBtn = this.querySelector('[data-reset]');

      this.popup = this.querySelector('[data-popup]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeBtn = this.querySelector('[data-close]');

      this.preview = this.querySelector('[data-preview]');
      this.previewHex = this.querySelector('[data-preview-hex]');
      this.sv = this.querySelector('[data-sv]');
      this.svThumb = this.querySelector('[data-sv-thumb]');
      this.hue = this.querySelector('[data-hue]');
      this.hueThumb = this.querySelector('[data-hue-thumb]');
      this.hexText = this.querySelector('[data-hex-text]');
      this.applyBtn = this.querySelector('[data-apply]');
      this.propertyInput = this.querySelector('[data-property-input]');

      if (!this.propertyInput || !this.trigger) return;

      this.finalName = this.propertyInput.dataset.finalName;
      this.isRequired = this.dataset.required === 'true';
      this.defaultColor = this.dataset.default || '#FFFFFF';
      this.chooseLabel = this.triggerText ? this.triggerText.textContent.trim() : 'Farbton wählen';

      // HSV-Zustand
      this.h = 0;
      this.s = 0;
      this.v = 1;
      this.hasSelection = false;

      if (this.isRequired) {
        // Pflichtfeld: Property-Name direkt setzen, damit der Wert mitgesendet wird
        this.propertyInput.name = this.finalName;
      }

      // Picker mit Startfarbe initialisieren
      this.setHsvFromHex(this.defaultColor);

      this.bindEvents();
      this.bindRequiredGuard();
    }

    /* Pflichtfeld: Submit blockieren, solange keine Farbe gewählt wurde */
    bindRequiredGuard() {
      if (!this.isRequired) return;
      const form = this.propertyInput.form;
      if (!form) return;
      form.addEventListener(
        'submit',
        (e) => {
          if (!this.hasSelection) {
            e.preventDefault();
            e.stopPropagation();
            this.open();
          }
        },
        true
      );
    }

    bindEvents() {
      this.trigger.addEventListener('click', () => this.open());
      this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop.addEventListener('click', () => this.close());
      this.applyBtn.addEventListener('click', () => this.close());
      this.resetBtn.addEventListener('click', () => this.reset());

      this.onKeydown = (e) => {
        if (e.key === 'Escape' && !this.popup.hidden) this.close();
      };

      // HEX-Eingabe
      this.hexText.addEventListener('input', (e) => {
        let value = e.target.value.trim();
        if (value && !value.startsWith('#')) {
          value = '#' + value;
          e.target.value = value;
        }
        if (this.isValidHex(value)) {
          this.hexText.setAttribute('aria-invalid', 'false');
          this.setHsvFromHex(value);
          this.commit();
        } else {
          this.hexText.setAttribute('aria-invalid', value === '' ? 'false' : 'true');
        }
      });

      this.hexText.addEventListener('blur', () => {
        // Ungültige Eingabe auf zuletzt gültigen Wert zurücksetzen
        this.hexText.value = this.currentHex();
        this.hexText.setAttribute('aria-invalid', 'false');
      });

      // Sättigung / Helligkeit – Pointer (Maus, Touch, Stift einheitlich)
      this.bindDrag(this.sv, (clientX, clientY) => {
        const rect = this.sv.getBoundingClientRect();
        this.s = clamp((clientX - rect.left) / rect.width, 0, 1);
        this.v = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
        this.updateFromHsv();
        this.commit();
      });

      // Farbton-Regler
      this.bindDrag(this.hue, (clientX) => {
        const rect = this.hue.getBoundingClientRect();
        this.h = clamp((clientX - rect.left) / rect.width, 0, 1) * 360;
        this.updateFromHsv();
        this.commit();
      });

      // Tastatursteuerung
      this.sv.addEventListener('keydown', (e) => this.handleSvKeys(e));
      this.hue.addEventListener('keydown', (e) => this.handleHueKeys(e));
    }

    /* Pointer-Drag-Helfer: gleiches Verhalten auf allen Geräten */
    bindDrag(el, onMove) {
      const move = (e) => onMove(e.clientX, e.clientY);
      const up = (e) => {
        el.releasePointerCapture?.(e.pointerId);
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', up);
      };
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        el.setPointerCapture?.(e.pointerId);
        move(e);
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', up);
        el.addEventListener('pointercancel', up);
      });
    }

    handleSvKeys(e) {
      const step = e.shiftKey ? 0.1 : 0.02;
      let handled = true;
      if (e.key === 'ArrowLeft') this.s = clamp(this.s - step, 0, 1);
      else if (e.key === 'ArrowRight') this.s = clamp(this.s + step, 0, 1);
      else if (e.key === 'ArrowUp') this.v = clamp(this.v + step, 0, 1);
      else if (e.key === 'ArrowDown') this.v = clamp(this.v - step, 0, 1);
      else handled = false;
      if (handled) {
        e.preventDefault();
        this.updateFromHsv();
        this.commit();
      }
    }

    handleHueKeys(e) {
      const step = e.shiftKey ? 20 : 4;
      let handled = true;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') this.h = clamp(this.h - step, 0, 360);
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') this.h = clamp(this.h + step, 0, 360);
      else handled = false;
      if (handled) {
        e.preventDefault();
        this.updateFromHsv();
        this.commit();
      }
    }

    /* ---- Zustand / Darstellung ---- */
    currentHex() {
      const [r, g, b] = hsvToRgb(this.h, this.s, this.v);
      return rgbToHex(r, g, b);
    }

    setHsvFromHex(hex) {
      const rgb = hexToRgb(hex);
      if (!rgb) return;
      [this.h, this.s, this.v] = rgbToHsv(rgb[0], rgb[1], rgb[2]);
      this.updateFromHsv();
    }

    /* Aktualisiert alle Picker-Elemente anhand des HSV-Zustands */
    updateFromHsv() {
      const hex = this.currentHex();

      // SV-Fläche bekommt den reinen Farbton als Hintergrund
      this.sv.style.backgroundColor = `hsl(${this.h}, 100%, 50%)`;

      // Thumb-Positionen
      this.svThumb.style.left = `${this.s * 100}%`;
      this.svThumb.style.top = `${(1 - this.v) * 100}%`;
      this.hueThumb.style.left = `${(this.h / 360) * 100}%`;
      this.hueThumb.style.backgroundColor = `hsl(${this.h}, 100%, 50%)`;

      // Vorschau + HEX-Feld
      this.preview.style.backgroundColor = hex;
      this.preview.style.backgroundImage = 'none';
      this.previewHex.textContent = hex;
      if (document.activeElement !== this.hexText) {
        this.hexText.value = hex;
      }
      this.hue.setAttribute('aria-valuenow', Math.round(this.h));
    }

    /* Übernimmt die aktuelle Farbe in Trigger + Property-Feld */
    commit() {
      const hex = this.currentHex();
      this.hasSelection = true;

      // Trigger-Vorschau
      this.swatch.style.backgroundColor = hex;
      this.swatch.dataset.hasColor = 'true';
      this.triggerText.textContent = hex;

      // Line Item Property
      this.propertyInput.name = this.finalName;
      this.propertyInput.value = hex;

      this.resetBtn.hidden = false;
    }

    reset() {
      this.hasSelection = false;
      if (!this.isRequired) {
        this.propertyInput.name = '';
      }
      this.propertyInput.value = '';

      this.swatch.dataset.hasColor = 'false';
      this.swatch.style.backgroundColor = '';
      this.triggerText.textContent = this.chooseLabel;
      this.resetBtn.hidden = true;

      // Picker auf Startfarbe zurücksetzen
      this.setHsvFromHex(this.defaultColor);
    }

    isValidHex(value) {
      return /^#[A-Fa-f0-9]{6}$/.test(value);
    }

    /* ---- Popup ---- */
    open() {
      // Picker auf den aktuell gewählten (oder Standard-) Wert setzen
      const startHex = this.hasSelection ? this.propertyInput.value : this.defaultColor;
      this.setHsvFromHex(startHex);

      this.popup.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('color-picker-open');
      document.addEventListener('keydown', this.onKeydown);
      // Fokus für Tastaturnutzer in den Dialog setzen
      requestAnimationFrame(() => this.sv.focus());
    }

    close() {
      this.popup.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('color-picker-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.trigger.focus();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('color-picker-open');
    }
  }

  customElements.define('color-picker-property', ColorPickerProperty);
}
